
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<template>
  <div>
    <!-- Verificar PRIMEIRO se é rota pública -->
    <div v-if="isPublicRoute" class="public-container">
      <router-view></router-view>
    </div>
    <div
      v-else-if="isAuthenticated == 'true'"
      class="app-container"
      :class="{ 'sidebar-collapsed': stateStore.sidebarCollapsed }"
    >
      <MainMenuNew />
      <MainHeader />
      <ThemeToggle />
      <div id="page-content">
        <router-view></router-view>
      </div>
      
      <!-- Modal de aviso de inatividade -->
      <InactivityWarning 
        :isVisible="isWarningVisible"
        :remainingTime="remainingTime"
        @continue="continueSession"
        @logout="performLogout"
      />
    </div>
    <div v-else>
      <Login />
    </div>
    <MainFooter v-if="!isPublicRoute" />
  </div>
</template>
<script>
import MainMenuNew from "./components/layout/MainMenuNew.vue";
import MainHeader from "./components/layout/MainHeader.vue";
import MainFooter from "./components/layout/MainFooter.vue";
import Login from "./views/Login/ViewLogin.vue";
import ThemeToggle from "./components/ThemeToggle.vue";
import InactivityWarning from "./components/InactivityWarning.vue";
import mainService from "./services/mainService";
import { useInactivityTimer } from "./composables/useInactivityTimer";
import stateStore from "./utils/store";
export default {
  name: "App",
  data: () => ({
    isAuthenticated: "false",
    isPublicRoute: false,
    stateStore,
  }),
  components: {
    MainMenuNew,
    MainHeader,
    MainFooter,
    Login,
    ThemeToggle,
    InactivityWarning,
  },
  setup() {
    const { 
      isWarningVisible, 
      remainingTime, 
      continueSession, 
      performLogout 
    } = useInactivityTimer();
    
    return {
      isWarningVisible,
      remainingTime,
      continueSession,
      performLogout
    };
  },
  async mounted() {
    this.isAuthenticated = localStorage.getItem("isAuthenticated");
    
    // VERIFICAÇÃO CRÍTICA: Verificar inatividade antes de qualquer coisa
    if (this.isAuthenticated === 'true') {
      this.checkForInactivityLogout();
    }
    
    // Configurar monitoramento de mudanças no localStorage
    this.setupStorageWatcher();

    // Login SPA: atualiza autenticação sem reload da página
    this.handleAuthChanged = (event) => {
      const authenticated = event?.detail?.authenticated === true
        || localStorage.getItem('isAuthenticated') === 'true';
      this.isAuthenticated = authenticated ? 'true' : 'false';

      if (authenticated) {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    };
    window.addEventListener('authChanged', this.handleAuthChanged);
    
    // Aguardar o router estar pronto antes de verificar a rota
    await this.$nextTick();
    this.checkPublicRoute();
    
    // Aplicar tema salvo ou preferência do sistema
    this.initializeTheme();
    
    // Carregar configurações do sistema
    await this.loadSystemSettings();
  },
  watch: {
    '$route'() {
      this.checkPublicRoute();
    }
  },
  methods: {
    checkPublicRoute() {
      // Verificar se a rota atual é pública (não requer autenticação)
      const currentRoute = this.$route;
      this.isPublicRoute = currentRoute && currentRoute.meta && currentRoute.meta.requiresNoAuth === true;
    },

    initializeTheme() {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        if (savedTheme === 'dark') {
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.removeAttribute('data-theme');
        }
      } else {
        // Verificar preferência do sistema
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.documentElement.setAttribute('data-theme', 'dark');
          localStorage.setItem('theme', 'dark');
        } else {
          localStorage.setItem('theme', 'light');
        }
      }
    },

    async loadSystemSettings() {
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          // Aplicar título do site
          if (response.data.siteTitle) {
            document.title = response.data.siteTitle;
          }
          
          // Aplicar favicon
          if (response.data.faviconUrl) {
            this.updateFavicon(response.data.faviconUrl);
          }
        }
      } catch (error) {
        console.log("Nenhuma configuração ativa encontrada");
      }
    },

    updateFavicon(faviconUrl) {
      // Remove favicon existente
      const existingFavicon = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
      if (existingFavicon) {
        existingFavicon.remove();
      }

      // Adiciona novo favicon
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.type = 'image/x-icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    },

    checkForInactivityLogout() {
      try {
        const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutos
        const lastActivity = localStorage.getItem('lastActivity');
        
        if (lastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
          
          // Se passou mais que o timeout de inatividade, fazer logout
          if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
            console.log('🚨 App.vue: Logout por inatividade prolongada:', timeSinceLastActivity / 1000, 'segundos');
            this.forceLogout();
            return;
          }
        } else {
          // Criar atividade inicial agora mesmo
          localStorage.setItem('lastActivity', Date.now().toString())
        }
        
        // Verificar se todas as abas foram fechadas
        const allTabsClosed = localStorage.getItem('allTabsClosed');
        
        if (allTabsClosed) {
          const closeTime = parseInt(allTabsClosed);
          const timeSinceClosure = Date.now() - closeTime;
          
          // Se houve fechamento de abas há mais de 5 minutos, fazer logout
          if (timeSinceClosure >= INACTIVITY_TIMEOUT) {
            localStorage.removeItem('allTabsClosed');
            this.forceLogout();
            return;
          } else {
            localStorage.removeItem('allTabsClosed');
          }
        }
      } catch (error) {
        console.error('❌ App.vue: Erro ao verificar inatividade:', error);
      }
    },

    forceLogout() {
      try {
        // Limpar todos os dados da sessão
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('currentRegion');
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('heartbeat');
        localStorage.removeItem('sessionStart');
        localStorage.removeItem('tabCount');
        localStorage.removeItem('allTabsClosed');
        
        // Atualizar estado local
        this.isAuthenticated = 'false';
        
        // Recarregar a página para garantir limpeza completa
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } catch (error) {
        console.error('Erro no logout forçado:', error);
        window.location.href = '/login';
      }
    },

    setupStorageWatcher() {
      // Monitorar mudanças no localStorage
      this.storageListener = (event) => {
        // Se a autenticação foi removida, atualizar estado
        if (event.key === 'isAuthenticated' && event.newValue !== 'true') {
          this.isAuthenticated = 'false';
        }
        
        // Se token foi removido, forçar logout
        if (event.key === 'token' && !event.newValue) {
          this.isAuthenticated = 'false';
        }
      };
      
      window.addEventListener('storage', this.storageListener);
    }
  },
  
  beforeUnmount() {
    // Limpar listener do storage
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    if (this.handleAuthChanged) {
      window.removeEventListener('authChanged', this.handleAuthChanged);
    }
  }
};
</script>

<style>
/* Layout adjustments for mobile */
.app-container {
  min-height: 100vh;
}

/* Ajustar conteúdo da página em mobile */
@media (max-width: 768px) {
  .app-container {
    padding-bottom: 70px; /* Bottom navigation height */
  }
  
  #page-content {
    min-height: calc(100vh - 130px); /* Ajustar altura considerando header e bottom nav */
    padding: 0;
  }
  
  /* Esconder menu lateral em mobile */
  .sidebar {
    display: none !important;
  }
  
  /* Ajustar largura do conteúdo quando sidebar está escondida */
  #page-content {
    margin-left: 0 !important;
    width: 100% !important;
  }
}

/* Container para rotas públicas */
.public-container {
  min-height: 100vh;
  width: 100%;
}

/* Desktop - manter layout original */
@media (min-width: 769px) {
  .app-container {
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
